import React, { useEffect, useMemo, useState } from 'react';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { CountryOption, getAirportCountries, LocationPayload } from '../thunks/getAirportsByCountry';

export const parseCountryCode = (value: string | string[] | null | undefined): string => {
  if (!value) {
    return '';
  }
  const raw = Array.isArray(value) ? value[0] : String(value);
  const upper = raw.trim().toUpperCase();
  if (/^1[A-Z]{2}$/.test(upper)) {
    return upper.slice(1);
  }
  if (/^[A-Z]{2}$/.test(upper)) {
    return upper;
  }
  return '';
};

type CountryCodeDropdownProps = {
  label: string;
  value: string | string[] | null | undefined;
  onChange: (countryCode: string) => void;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  placeholder?: string;
  className?: string;
  hideLabel?: boolean;
};

const CountryCodeDropdown = ({
  label,
  value,
  onChange,
  disabled = false,
  required = false,
  error = false,
  placeholder = 'Select country code',
  className,
  hideLabel = false,
}: CountryCodeDropdownProps) => {
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const selectedCode = parseCountryCode(value);

  useEffect(() => {
    let cancelled = false;
    getAirportCountries().then((result) => {
      if (!cancelled) {
        setCountries(result);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedCountry = useMemo(
    () => countries.find((country) => country.countryCode === selectedCode) || null,
    [countries, selectedCode]
  );

  return (
    <Autocomplete
      size="small"
      options={countries}
      disabled={disabled}
      className={className}
      style={{ background: 'white' }}
      value={selectedCountry}
      getOptionLabel={(option) => option?.customizedLabel || option?.countryCode || ''}
      getOptionSelected={(option, selected) => option.countryCode === selected.countryCode}
      onChange={(_event, country) => onChange(country?.countryCode || '')}
      renderInput={(params) => (
        <TextField
          {...params}
          label={hideLabel ? undefined : label}
          placeholder={placeholder}
          variant="outlined"
          required={required}
          error={error}
          className="form-control cm-input custom-disabled-field"
        />
      )}
    />
  );
};

export default CountryCodeDropdown;

export const LocationPayloadPreview = ({
  title,
  payload,
}: {
  title: string;
  payload: LocationPayload | null;
}) => {
  if (!payload) {
    return null;
  }

  return (
    <div className="p-2" style={{ background: '#f7f7f7', fontSize: 12, maxHeight: 220, overflow: 'auto' }}>
      <div className="mb-1">
        <strong>{title}</strong> {payload.countryCode} ({payload.airports.length})
      </div>
      {payload.airports.slice(0, 20).map((airport) => (
        <div key={airport.airportCode}>
          {airport.airportCode}
          {airport.otherAirportCodes.length ? ` / ${airport.otherAirportCodes.join(',')}` : ''}
          {airport.iataRegionCode ? ` / IATA ${airport.iataRegionCode}` : ''}
        </div>
      ))}
      {payload.airports.length > 20 ? `… ${payload.airports.length - 20} more` : ''}
    </div>
  );
};
